const DEFAULT_AT_ONCE = 4;

// Debounces an async function: repeated calls within `delayMs` collapse into a single call of
// `fn` with the LATEST args once the window elapses, and every call made during that window
// (not just the last) resolves/rejects together with that one result. Useful for wrapping an
// expensive/rate-limited async operation (a network call) that's driven by something with no
// debounce of its own -- e.g. a live-validation effect that re-runs on every keystroke.
export const debounceAsync = <Args extends unknown[], R>(
  fn: (...args: Args) => Promise<R>,
  delayMs: number
): ((...args: Args) => Promise<R>) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let resolvers: ((value: R) => void)[] = [];
  let rejecters: ((reason: unknown) => void)[] = [];

  return (...args: Args): Promise<R> =>
    new Promise<R>((resolve, reject) => {
      if (timer) {
        clearTimeout(timer);
      }
      resolvers.push(resolve);
      rejecters.push(reject);
      timer = setTimeout(() => {
        const pendingResolvers = resolvers;
        const pendingRejecters = rejecters;
        resolvers = [];
        rejecters = [];
        fn(...args)
          .then(result => pendingResolvers.forEach(resolveOne => resolveOne(result)))
          .catch(error => pendingRejecters.forEach(rejectOne => rejectOne(error)));
      }, delayMs);
    });
};

// Caches the single most recent call's args/result (compared by JSON.stringify, so args must be
// JSON-serializable) and returns that same result for a later call with identical args, without
// re-invoking `fn` -- no TTL; the cached result lives until the args change, the call rejects, or
// the wrapper is discarded.
export const memoizeLastAsync = <Args extends unknown[], R>(
  fn: (...args: Args) => Promise<R>
): ((...args: Args) => Promise<R>) => {
  let lastKey: string | undefined;
  let lastResult: Promise<R> | undefined;

  return (...args: Args): Promise<R> => {
    let key: string;
    try {
      key = JSON.stringify(args);
    } catch {
      // Unkeyable args (e.g. circular references) -- bypass the cache rather than throw
      // synchronously out of what callers expect to be a Promise-returning function.
      return fn(...args);
    }
    if (key === lastKey && lastResult) {
      return lastResult;
    }
    lastKey = key;
    lastResult = fn(...args).catch(error => {
      // Don't cache a rejection -- a repeat call with the same args should retry, not replay the
      // same failure.
      if (lastKey === key) {
        lastKey = undefined;
        lastResult = undefined;
      }
      throw error;
    });
    return lastResult;
  };
};

export const artificialWait = (time: number): Promise<void> =>
  new Promise(resolve => {
    setTimeout(() => resolve(), time);
  });

// Like Promise.all, but it only dispatches a few at a time. This is a thread-pool queueing design pattern.
export const staggeredAll = (
  promises: (() => Promise<any>)[],
  atATime = DEFAULT_AT_ONCE
): Promise<any[]> =>
  new Promise(resolveItAll => {
    const results: any[] = [];
    let promiseIndex = 0;
    let completedCount = 0;

    if (!promises.length) {
      resolveItAll([]);
    }

    const makePromise = async index => {
      const promise = promises[index];
      const res = await promise();

      results[index] = res;
      if (++completedCount === promises.length) {
        resolveItAll(results);
      } else if (promiseIndex < promises.length) {
        makePromise(promiseIndex++);
      }
    };
    for (let i = 0; i < atATime; ++i) {
      if (promiseIndex >= promises.length) {
        break;
      }
      makePromise(promiseIndex++);
    }
  });
